import { Link } from "react-router-dom";
import { Check, X, Phone, ArrowRight, ShieldCheck, Zap, Layers, Globe, Code2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface ComparisonRow {
  dimension: string;
  category: string;
  icon: typeof Zap;
  template: {
    status: false;
    title: string;
    details: string;
  };
  platform: {
    status: true;
    title: string;
    details: string;
  };
}

const comparisonData: ComparisonRow[] = [
  {
    dimension: "Code Ownership & Freedom",
    category: "Ownership",
    icon: Code2,
    template: {
      status: false,
      title: "Rented SaaS Prison",
      details: "Locked into Wix, Squarespace, or generic builders. If you cancel your monthly subscription, your website vanishes overnight.",
    },
    platform: {
      status: true,
      title: "100% Asset Ownership",
      details: "You own 100% of your source code, domain, and data. Custom React 18 / Vite stack exportable to any cloud host with zero platform ransom.",
    },
  },
  {
    dimension: "Google Search & Local SEO",
    category: "SEO Power",
    icon: Globe,
    template: {
      status: false,
      title: "Single URL Limitation",
      details: "One flat page cannot rank for multiple services or towns. Google has only one page to index, suffocating your local search reach.",
    },
    platform: {
      status: true,
      title: "Multi-Page Topical Dominance",
      details: "Dedicated, keyword-targeted semantic URLs for every service and Michigan municipality. Engineered for top-3 Google Map Pack and organic rankings.",
    },
  },
  {
    dimension: "Speed & Core Web Vitals",
    category: "Performance",
    icon: Zap,
    template: {
      status: false,
      title: "Bloated 3–6s Load Times",
      details: "Heaped with generic third-party plugins and drag-and-drop script bloat. Fails Google mobile speed checks and penalizes ad traffic.",
    },
    platform: {
      status: true,
      title: "Sub-Second Edge Speeds",
      details: "Instantaneous transitions (<800ms load). 95+ Core Web Vitals performance that converts impatient mobile visitors and lowers ad costs.",
    },
  },
  {
    dimension: "Operational Capabilities",
    category: "Functionality",
    icon: Layers,
    template: {
      status: false,
      title: "Basic Email Form Only",
      details: "Static contact box with zero workflow integration. Incapable of scaling into client portals, custom booking, or interactive apps.",
    },
    platform: {
      status: true,
      title: "Full Business Web OS",
      details: "Engineered with client portals, CRM pipelines, real-time audio visualizers, interactive community walls, and automated payment gateways.",
    },
  },
  {
    dimension: "Mobile Conversion & Calls",
    category: "Conversions",
    icon: Phone,
    template: {
      status: false,
      title: "Passive, Hidden Numbers",
      details: "Tiny header text that requires pinching and zooming. No fixed 1-tap mobile dialer or lead attribution tracking.",
    },
    platform: {
      status: true,
      title: "Frictionless 1-Tap Calling",
      details: "High-contrast docked mobile call bar directly to Dallas Valentine, multi-touch attribution, and friction-free lead capture forms.",
    },
  },
  {
    dimension: "Engineering & Direct Support",
    category: "Support",
    icon: Users,
    template: {
      status: false,
      title: "Chatbots & Offshore Tickets",
      details: "Automated forum replies or freelancers who vanish after sending a template invoice. No local accountability.",
    },
    platform: {
      status: true,
      title: "Direct Michigan Partner",
      details: "Direct line to Dallas Valentine and our dedicated engineering team in Ubly, MI. We treat your platform like our own equity.",
    },
  },
];

interface Props {
  className?: string;
  showCTA?: boolean;
}

export const PlatformVsTemplateComparison = ({ className = "", showCTA = true }: Props) => {
  return (
    <section id="stop-renting" className={`py-16 md:py-24 bg-background relative overflow-hidden ${className}`}>
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
          <Badge className="mb-4 bg-accent/15 text-accent border border-accent/20 font-semibold px-3 py-1">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
            The Architectural Divide
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
            Why Rented 1-Page Templates Fail <br className="hidden sm:inline" />
            <span className="text-gradient">Against a Custom Business Web OS</span>
          </h2>
          <p className="mt-4 text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Cheap template mills sell you a rented digital flyer. We engineer bespoke, multi-page platforms that position your business as the undisputed leader in your market.
          </p>
        </div>

        {/* Comparison Grid */}
        <div className="max-w-5xl mx-auto">
          {/* Header Row for Desktop */}
          <div className="hidden lg:grid grid-cols-12 gap-4 pb-4 border-b border-border/80 text-sm font-bold uppercase tracking-wider text-muted-foreground">
            <div className="col-span-4">Evaluation Dimension</div>
            <div className="col-span-4 text-red-500/90 flex items-center gap-1.5">
              <X className="h-4 w-4" />
              Cheap 1-Page Rented Template
            </div>
            <div className="col-span-4 text-accent flex items-center gap-1.5">
              <Check className="h-4 w-4" />
              Valentine Digital Bespoke Platform
            </div>
          </div>

          {/* Rows */}
          <div className="space-y-4 lg:space-y-0 lg:divide-y lg:divide-border/60">
            {comparisonData.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.dimension}
                  className="rounded-2xl lg:rounded-none border border-border/70 lg:border-none p-5 lg:py-6 lg:px-2 bg-card lg:bg-transparent grid grid-cols-1 lg:grid-cols-12 gap-4 items-center transition-colors hover:bg-muted/20"
                >
                  {/* Dimension */}
                  <div className="lg:col-span-4 flex items-start gap-3">
                    <div className="h-9 w-9 rounded-xl bg-accent/10 border border-accent/20 text-accent flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <span className="text-xs uppercase font-semibold tracking-wider text-accent/80 block lg:hidden">
                        {item.category}
                      </span>
                      <h3 className="font-bold text-foreground text-base">{item.dimension}</h3>
                    </div>
                  </div>

                  {/* Template Column (Bad) */}
                  <div className="lg:col-span-4 rounded-xl lg:rounded-none bg-red-500/5 lg:bg-transparent border border-red-500/10 lg:border-none p-4 lg:p-0">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold text-sm mb-1">
                      <X className="h-4 w-4 shrink-0" />
                      <span>{item.template.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {item.template.details}
                    </p>
                  </div>

                  {/* Valentine Platform Column (Superior) */}
                  <div className="lg:col-span-4 rounded-xl lg:rounded-none bg-accent/10 lg:bg-transparent border border-accent/20 lg:border-none p-4 lg:p-0">
                    <div className="flex items-center gap-2 text-accent font-bold text-sm mb-1">
                      <Check className="h-4 w-4 shrink-0 text-accent" />
                      <span>{item.platform.title}</span>
                    </div>
                    <p className="text-xs text-foreground/80 leading-relaxed font-medium">
                      {item.platform.details}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Card */}
        {showCTA && (
          <div className="max-w-4xl mx-auto mt-12 md:mt-16">
            <Card className="border-accent/30 bg-gradient-to-br from-primary via-primary/95 to-slate-900 p-8 md:p-10 text-primary-foreground shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl -z-10 pointer-events-none" />
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left space-y-2">
                  <Badge className="bg-accent text-accent-foreground font-bold">
                    Stop Renting Cheap Code
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    Ready to Own a High-Performance Digital System?
                  </h3>
                  <p className="text-primary-foreground/75 text-sm md:text-base max-w-xl">
                    Speak directly with Dallas Valentine. We will review your current online footprint, map out your custom multi-page architecture, and provide a clear roadmap.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto shrink-0">
                  <Button size="lg" variant="glow" asChild className="w-full">
                    <a href="tel:+19898432600" className="flex items-center justify-center gap-2">
                      <Phone className="h-4 w-4" />
                      Call (989) 843-2600
                    </a>
                  </Button>
                  <Button size="lg" variant="ghost" className="border border-white/20 text-white hover:bg-white/10 w-full" asChild>
                    <Link to="/contact" className="flex items-center justify-center gap-2">
                      Book Consultation
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default PlatformVsTemplateComparison;
